import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { Pressable, TextStyle, View, ViewStyle } from "react-native";
import { AlertType, Hoverable, Icon, Text } from "..";
import { StyleFunction, useThemedStyle } from "../..";

export function Tabs({
    labels = [],
    children,
    index: initialIndex = 0,
}: {
    labels: (
        | string
        | [string, { color: TextStyle["color"]; type: AlertType } | undefined]
    )[];
    children: ReactNode[];
    index?: number;
}) {
    const [index, setIndex] = useState(
        initialIndex >= 0 && initialIndex < labels.length ? initialIndex : 0
    );
    const styles = useThemedStyle(styleFunc);

    const filteredChildren = useMemo(() => {
        if (!children) {
            return null;
        }

        return children.filter(Boolean);
    }, [children]);

    const filteredLabels = useMemo(() => {
        if (!labels) {
            return null;
        }

        return labels.filter(
            itr =>
                (typeof itr === "string" && itr) ||
                (typeof itr === "object" && itr[0])
        );
    }, [labels]);

    if (filteredChildren && index >= filteredChildren.length) {
        console.warn(`Tab with index ${index} does not exist`);
        return null;
    }

    if (filteredLabels?.length !== filteredChildren?.length) {
        console.warn("An error occured while rendering tabs.");
        return null;
    }

    return (
        <>
            <View style={styles.header}>
                {filteredLabels.map((label, idx) => (
                    <Tab
                        key={idx}
                        index={idx}
                        label={typeof label !== "string" ? label[0] : label}
                        badge={typeof label !== "string" ? label[1] : undefined}
                        selected={idx === index}
                        onPress={setIndex}
                        testID={`tab:${label}`}
                    />
                ))}
            </View>
            {filteredChildren ? filteredChildren[index] : null}
        </>
    );
}

function Tab({
    index,
    label,
    badge,
    selected,
    onPress,
    testID,
}: {
    index: number;
    label: string;
    selected: boolean;
    onPress: (index: number) => void;
    badge?: { color: TextStyle["color"]; type: AlertType };
    testID?: string;
}) {
    const styles = useThemedStyle(styleFunc);
    const handlePress = useCallback(() => {
        onPress(index);
    }, [index, onPress]);

    return (
        <Hoverable>
            {isHovered => (
                <Pressable
                    onPress={handlePress}
                    style={[
                        styles.headerItem,
                        isHovered ? styles.hoveredItem : undefined,
                        selected && styles.activeTab,
                    ]}
                    testID={testID}
                >
                    <View style={styles.labelRow}>
                        <Text
                            style={[
                                styles.headerItemText,
                                selected && styles.activeTabText,
                            ]}
                        >
                            {label}
                        </Text>
                        {badge ? (
                            <>
                                <Icon
                                    color={badge.color}
                                    name={badge.type}
                                    style={styles.iconSpacing}
                                />
                            </>
                        ) : null}
                    </View>
                </Pressable>
            )}
        </Hoverable>
    );
}

const styleFunc: StyleFunction = theme => ({
    header: {
        ...theme.styles.row,
        marginBottom: theme.spacingScale * 2,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.grey250,
    },
    headerItem: {
        paddingHorizontal: theme.spacingScale * 1.5,
        paddingTop: theme.spacingScale / 2,
    },
    headerItemText: {
        ...(theme.headlines.h5 as ViewStyle),
        marginBottom: 0,
    },
    activeTab: {
        borderBottomColor: theme.colors.secondary,
        borderBottomWidth: 2,
    },
    activeTabText: {
        color: theme.colors.secondary,
    },
    hoveredItem: {
        backgroundColor: theme.colors.grey100,
    },
    labelRow: {
        ...theme.styles.row,
        alignItems: "center",
    },
    iconSpacing: { marginLeft: 3 },
});
